import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Upload, GripVertical, Trash2, Plus, Save, ChevronDown, ChevronRight,
  User, Image, Type, Link2, Award, Minus, FileText, Code, Sparkles,
  ArrowLeft, Loader2, ImagePlus, Globe, MessageSquare, Copy, ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";

const API = '/api';

/* ── Block type definitions ── */
const BLOCK_TYPES = {
  HEADER_TEXT:        { label: "Header Text",       icon: Type,           color: "#5E5E5E" },
  SENDER_DETAILS:    { label: "Sender Details",     icon: User,           color: "#0F6CBD" },
  COMPANY_LOGO:      { label: "Company Logo",       icon: Image,          color: "#107C10" },
  SOCIAL_ICONS:      { label: "Social Icons",       icon: Globe,          color: "#0F6CBD" },
  SOCIAL_LINKS:      { label: "Social Links",       icon: Globe,          color: "#0F6CBD" },
  TEXT:              { label: "Text",               icon: Type,           color: "#171717" },
  PROMOTIONAL_TEXT:   { label: "Promotional Text",   icon: MessageSquare,  color: "#D83B01" },
  IMAGE_BANNER:      { label: "Image Banner",       icon: ImagePlus,      color: "#D83B01" },
  BANNER:            { label: "Campaign Banner",    icon: ImagePlus,      color: "#D83B01" },
  DIVIDER:           { label: "Divider",            icon: Minus,          color: "#E1DFDD" },
  SPACER:            { label: "Spacer",             icon: Minus,          color: "#E1DFDD" },
  LEGAL_TEXT:        { label: "Legal Text",         icon: FileText,       color: "#5E5E5E" },
  MEMBERSHIP_BADGES: { label: "Badges / Certs",     icon: Award,          color: "#107C10" },
};

function createNewBlock(type) {
  const defaults = {
    HEADER_TEXT:     { content: "Enter header text...", settings: { fontSize: "10px", color: "#333333" } },
    SENDER_DETAILS:  { content: { name: "{{displayName}}", title: "{{jobTitle}}", company: "{{companyName}}", phone: "{{phone}}", fax: "", mobile: "{{mobile}}", email: "{{mail}}", website: "{{website}}", address: "{{office}}" }, settings: { layout: "two-column" } },
    COMPANY_LOGO:    { content: { url: "", alt: "Company Logo", href: "", width: 180 }, settings: {} },
    SOCIAL_ICONS:    { content: "", items: [{ platform: "LinkedIn", url: "", label: "LinkedIn" }], settings: { gap_px: 8 } },
    PROMOTIONAL_TEXT: { content: "Enter promotional text...", settings: { fontSize: "12px", color: "#0F6CBD" } },
    IMAGE_BANNER:    { content: { url: "", alt: "Banner", href: "", width: "100%" }, settings: {} },
    DIVIDER:         { content: "", settings: { color: "#E1DFDD", width: "100%" } },
    SPACER:          { content: "", settings: { height: "12px" } },
    LEGAL_TEXT:      { content: "Enter legal disclaimer...", settings: { fontSize: "10px", color: "#999999" } },
    MEMBERSHIP_BADGES: { content: "", items: [{ url: "", alt: "Badge", href: "", height: 40 }], settings: { gap_px: 8 } },
  };
  const d = defaults[type] || { content: "", settings: {} };
  return {
    id: `block_${Math.random().toString(36).slice(2, 8)}`,
    type,
    name: BLOCK_TYPES[type]?.label || type,
    content: d.content,
    settings: d.settings || {},
    items: d.items || [],
  };
}

/* ── Sample data for preview ── */
const SAMPLE_DATA = {
  "{{displayName}}": "Rok Kobal", "{{jobTitle}}": "CEO", "{{companyName}}": "Europacific, Logistika d.o.o.",
  "{{phone}}": "+386-5-6626-381", "{{mobile}}": "+386-40-793-000", "{{mail}}": "rok.kobal@europacific.com",
  "{{website}}": "www.europacific.com", "{{office}}": "Ankaranska cesta 5, 6000 Koper",
  "{{department}}": "Management", "{{givenName}}": "Rok", "{{surname}}": "Kobal",
};
function resolveSample(val) {
  if (!val || typeof val !== "string") return val || "";
  return val.replace(/\{\{(\w+)\}\}/g, (m) => SAMPLE_DATA[m] || m);
}
function isVariable(val) { return typeof val === "string" && val.includes("{{"); }

/* ── Block preview renderer ── */
function renderBlockPreview(block) {
  const s = block.settings || {};
  switch (block.type) {
    case "HEADER_TEXT":
      return `<p style="margin:0;font-size:${s.fontSize||"10px"};color:${s.color||"#333"};font-family:Arial,sans-serif;line-height:1.4;">${esc(resolveSample(str(block.content)))}</p>`;
    case "SENDER_DETAILS": {
      const c = typeof block.content === "object" && block.content ? block.content : {};
      const layout = s.layout || "two-column";
      const r = (v) => esc(resolveSample(v || ""));
      if (layout === "two-column") {
        const left = [c.name && `<strong style="font-size:14px;">${r(c.name)}</strong>`, c.title && `<span style="font-size:12px;color:#5E5E5E;">${r(c.title)}</span>`, c.company && `<span style="font-size:12px;color:#5E5E5E;">${r(c.company)}</span>`].filter(Boolean).join("<br/>");
        const right = [c.phone && `<span style="font-size:12px;">T: ${r(c.phone)}</span>`, c.fax && `<span style="font-size:12px;">F: ${r(c.fax)}</span>`, c.mobile && `<span style="font-size:12px;">M: ${r(c.mobile)}</span>`, c.email && `<span style="font-size:12px;">E: ${r(c.email)}</span>`, c.website && `<span style="font-size:12px;">&nbsp;&nbsp;&nbsp;${r(c.website)}</span>`, c.address && `<span style="font-size:12px;color:#5E5E5E;white-space:pre-line;">${r(c.address)}</span>`].filter(Boolean).join("<br/>");
        return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,sans-serif;"><tr><td style="vertical-align:top;padding-right:24px;border-right:1px solid #E1DFDD;">${left}</td><td style="vertical-align:top;padding-left:16px;">${right}</td></tr></table>`;
      }
      const parts = [c.name && `<strong>${esc(c.name)}</strong>`, c.title, c.company, c.phone && `T: ${c.phone}`, c.email, c.website].filter(Boolean);
      return `<div style="font-family:Arial,sans-serif;font-size:13px;line-height:1.6;">${parts.map(p => esc(p)).join("<br/>")}</div>`;
    }
    case "COMPANY_LOGO": {
      const c = typeof block.content === "object" ? block.content : {};
      const url = c.url || "";
      if (!url) return `<div style="width:${c.width||180}px;height:60px;background:#F3F2F1;display:flex;align-items:center;justify-content:center;font-size:11px;color:#5E5E5E;border:1px dashed #E1DFDD;">[Logo placeholder]</div>`;
      const img = `<img src="${esc(url)}" alt="${esc(c.alt||"")}" width="${c.width||180}" style="display:block;border:0;" />`;
      return c.href ? `<a href="${esc(c.href)}" target="_blank" style="text-decoration:none;">${img}</a>` : img;
    }
    case "SOCIAL_ICONS": {
      const items = block.items || [];
      if (!items.length) return `<span style="font-size:11px;color:#999;">[Social icons]</span>`;
      const icons = items.map(i => `<td style="padding-right:${s.gap_px||8}px;"><a href="${esc(i.url||"#")}" target="_blank" style="font-size:12px;color:#0F6CBD;text-decoration:none;">${esc(i.label||i.platform||"Link")}</a></td>`).join("");
      return `<table cellpadding="0" cellspacing="0" border="0"><tr>${icons}</tr></table>`;
    }
    case "PROMOTIONAL_TEXT":
      return `<div style="margin:0;font-size:${s.fontSize||"12px"};color:${s.color||"#0F6CBD"};font-family:Arial,sans-serif;line-height:1.5;">${esc(str(block.content)).replace(/\n/g, "<br/>")}</div>`;
    case "IMAGE_BANNER": {
      const c = typeof block.content === "object" ? block.content : {};
      const url = c.url || "";
      if (!url) return `<div style="width:100%;height:80px;background:#F3F2F1;display:flex;align-items:center;justify-content:center;font-size:11px;color:#5E5E5E;border:1px dashed #E1DFDD;">[Banner placeholder]</div>`;
      const img = `<img src="${esc(url)}" alt="${esc(c.alt||"")}" width="${c.width||"100%"}" style="display:block;border:0;max-width:600px;" />`;
      return c.href ? `<a href="${esc(c.href)}" target="_blank">${img}</a>` : img;
    }
    case "DIVIDER":
      return `<hr style="border:none;border-top:1px solid ${s.color||"#E1DFDD"};width:${s.width||"100%"};margin:8px 0;" />`;
    case "SPACER":
      return `<div style="height:${s.height||"12px"};line-height:${s.height||"12px"};font-size:1px;">&nbsp;</div>`;
    case "LEGAL_TEXT":
      return `<p style="margin:0;font-size:${s.fontSize||"10px"};color:${s.color||"#999"};font-family:Arial,sans-serif;line-height:1.4;">${esc(str(block.content)).replace(/\n/g, "<br/>")}</p>`;
    case "MEMBERSHIP_BADGES": {
      const items = block.items || [];
      if (!items.length) return `<span style="font-size:11px;color:#999;">[Badges]</span>`;
      const badges = items.map(i => {
        const img = i.url ? `<img src="${esc(i.url)}" alt="${esc(i.alt||"")}" height="${i.height||40}" style="display:block;border:0;" />` : `<span style="display:inline-block;width:60px;height:${i.height||40}px;background:#F3F2F1;font-size:9px;text-align:center;line-height:${i.height||40}px;color:#5E5E5E;">${esc(i.alt||"Badge")}</span>`;
        return `<td style="padding-right:${s.gap_px||8}px;">${i.href ? `<a href="${esc(i.href)}" target="_blank">${img}</a>` : img}</td>`;
      }).join("");
      return `<table cellpadding="0" cellspacing="0" border="0"><tr>${badges}</tr></table>`;
    }
    default:
      // Handle legacy/alias types
      if (block.type === "SOCIAL_LINKS") {
        const items = block.items || [];
        if (!items.length) return `<span style="font-size:11px;color:#999;">[Social links]</span>`;
        const icons = items.map(i => `<td style="padding-right:${s.gap_px||8}px;"><a href="${esc(i.url||"#")}" target="_blank" style="font-size:12px;color:#0F6CBD;text-decoration:none;">${esc(i.label||i.platform||"Link")}</a></td>`).join("");
        return `<table cellpadding="0" cellspacing="0" border="0"><tr>${icons}</tr></table>`;
      }
      if (block.type === "TEXT") {
        const text = resolveSample(str(block.content));
        return `<div style="margin:0;font-size:${s.fontSize||s.font_size||"12px"};color:${s.color||"#171717"};font-family:Arial,sans-serif;line-height:1.5;white-space:pre-line;">${esc(text)}</div>`;
      }
      if (block.type === "BANNER") {
        return `<div style="width:100%;height:60px;background:#F3F2F1;display:flex;align-items:center;justify-content:center;font-size:11px;color:#5E5E5E;border:1px dashed #E1DFDD;">[Campaign banner slot]</div>`;
      }
      if (block.type === "MEMBERSHIP_BADGE_ROW") {
        const items = block.items || [];
        if (!items.length) return `<span style="font-size:11px;color:#999;">[Badges]</span>`;
        const badges = items.map(i => `<td style="padding-right:8px;"><img src="${esc(i.url||"")}" alt="${esc(i.alt||"")}" height="${i.height||40}" style="border:0;" /></td>`).join("");
        return `<table cellpadding="0" cellspacing="0" border="0"><tr>${badges}</tr></table>`;
      }
      if (block.type === "LOGO_GROUP" || block.type === "LINKED_ICON_ROW") {
        const items = block.items || [];
        if (!items.length) return `<span style="font-size:11px;color:#999;">[${block.type}]</span>`;
        const imgs = items.map(i => `<td style="padding-right:8px;"><img src="${esc(i.url||"")}" alt="${esc(i.alt||"")}" width="${i.width||60}" style="border:0;" /></td>`).join("");
        return `<table cellpadding="0" cellspacing="0" border="0"><tr>${imgs}</tr></table>`;
      }
      return `<p style="font-size:12px;color:#999;">[${block.type}]</p>`;
  }
}

function esc(s) { return (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;"); }
function str(v) { return typeof v === "string" ? v : typeof v === "object" ? JSON.stringify(v) : String(v || ""); }

function arrayMove(arr, from, to) {
  const copy = [...arr];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
}

/* ── Sortable block item ── */
function SortableBlock({ block, isSelected, onSelect, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const typeDef = BLOCK_TYPES[block.type] || { label: block.type, icon: Code, color: "#5E5E5E" };
  const Icon = typeDef.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-sm transition-colors ${isSelected ? "border-[#0F6CBD] bg-[#EBF3FC]" : "border-[#E1DFDD] bg-white hover:border-[#0F6CBD]/40"}`}
      data-testid={`builder-block-${block.id}`}
    >
      <div className="flex items-center gap-2 px-3 py-2 cursor-pointer" onClick={() => onSelect(block.id)}>
        <button {...attributes} {...listeners} className="cursor-grab text-[#5E5E5E] hover:text-[#171717] shrink-0" data-testid={`drag-handle-${block.id}`}>
          <GripVertical size={14} />
        </button>
        <div className="w-1.5 h-5 rounded-sm shrink-0" style={{ backgroundColor: typeDef.color }} />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-[#171717] truncate">{block.name}</p>
          <p className="text-[10px] text-[#5E5E5E] font-mono">{block.type}</p>
        </div>
        {isSelected ? <ChevronDown size={14} className="text-[#5E5E5E]" /> : <ChevronRight size={14} className="text-[#5E5E5E]" />}
        <button onClick={(e) => { e.stopPropagation(); onDelete(block.id); }} className="text-[#5E5E5E] hover:text-[#A4262C] shrink-0" data-testid={`delete-block-${block.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

/* ── Block config panel ── */
function BlockConfig({ block, onChange, assets }) {
  const update = (field, value) => onChange({ ...block, [field]: value });
  const updateContent = (key, value) => {
    const content = typeof block.content === "object" && block.content ? { ...block.content } : {};
    content[key] = value;
    update("content", content);
  };
  const updateSetting = (key, value) => update("settings", { ...block.settings, [key]: value });
  const updateItem = (idx, key, value) => {
    const items = [...(block.items || [])];
    items[idx] = { ...items[idx], [key]: value };
    update("items", items);
  };
  const addItem = (template) => update("items", [...(block.items || []), template]);
  const removeItem = (idx) => update("items", (block.items || []).filter((_, i) => i !== idx));

  const inputCls = "bg-white border border-[#E1DFDD] rounded-sm px-2 py-1.5 text-xs text-[#171717] focus:outline-none focus:border-[#0F6CBD] w-full";
  const labelCls = "text-[10px] font-semibold uppercase tracking-wider text-[#5E5E5E] mb-1 block";

  return (
    <div className="p-3 space-y-3 border-t border-[#E1DFDD] bg-[#FAFAFA]" data-testid={`config-${block.id}`}>
      <div>
        <label className={labelCls}>Block Name</label>
        <input value={block.name} onChange={(e) => update("name", e.target.value)} className={inputCls} />
      </div>

      {/* SENDER_DETAILS */}
      {block.type === "SENDER_DETAILS" && (() => {
        const c = typeof block.content === "object" && block.content ? block.content : {};
        return (
          <div className="space-y-2">
            <div className="bg-[#EBF3FC] border border-[#0F6CBD]/20 rounded-sm p-2 text-[10px] text-[#0F6CBD]">
              Dynamic block — uses <code className="font-mono">{`{{variable}}`}</code> placeholders filled from each sender's Graph/Entra profile at send time. Static text is also supported.
            </div>
            <div>
              <label className={labelCls}>Layout</label>
              <select value={block.settings?.layout || "two-column"} onChange={(e) => updateSetting("layout", e.target.value)} className={inputCls}>
                <option value="two-column">Two Column</option>
                <option value="stacked">Stacked</option>
              </select>
            </div>
            {[
              { key: "name", label: "Name", ph: "{{displayName}}" },
              { key: "title", label: "Title", ph: "{{jobTitle}}" },
              { key: "company", label: "Company", ph: "{{companyName}}" },
              { key: "phone", label: "Phone", ph: "{{phone}}" },
              { key: "fax", label: "Fax", ph: "" },
              { key: "mobile", label: "Mobile", ph: "{{mobile}}" },
              { key: "email", label: "Email", ph: "{{mail}}" },
              { key: "website", label: "Website", ph: "{{website}}" },
            ].map(({ key, label, ph }) => (
              <div key={key}>
                <label className={labelCls}>
                  {label}
                  {isVariable(c[key]) && <span className="ml-1 text-[#0F6CBD] font-normal">(dynamic)</span>}
                </label>
                <input value={c[key] || ""} onChange={(e) => updateContent(key, e.target.value)} className={`${inputCls} ${isVariable(c[key]) ? "border-[#0F6CBD]/40 bg-[#EBF3FC]/30" : ""}`} placeholder={ph || `Static ${label.toLowerCase()}`} />
              </div>
            ))}
            <div>
              <label className={labelCls}>Address</label>
              <textarea value={c.address || ""} onChange={(e) => updateContent("address", e.target.value)} rows={2} className={inputCls} placeholder="{{office}}" />
            </div>
            <div className="text-[9px] text-[#5E5E5E] border-t border-[#E1DFDD] pt-2 mt-2">
              <span className="font-semibold">Available variables:</span> {`{{displayName}} {{jobTitle}} {{department}} {{companyName}} {{mail}} {{phone}} {{mobile}} {{office}} {{website}}`}
            </div>
          </div>
        );
      })()}

      {/* Text blocks */}
      {["HEADER_TEXT", "PROMOTIONAL_TEXT", "LEGAL_TEXT", "TEXT"].includes(block.type) && (
        <div className="space-y-2">
          <div>
            <label className={labelCls}>Content</label>
            <textarea value={str(block.content)} onChange={(e) => update("content", e.target.value)} rows={4} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Font Size</label>
              <input value={block.settings?.fontSize || "12px"} onChange={(e) => updateSetting("fontSize", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Color</label>
              <input value={block.settings?.color || "#333333"} onChange={(e) => updateSetting("color", e.target.value)} className={inputCls} />
            </div>
          </div>
        </div>
      )}

      {/* Logo / Banner */}
      {["COMPANY_LOGO", "IMAGE_BANNER", "BANNER"].includes(block.type) && (() => {
        const c = typeof block.content === "object" && block.content ? block.content : {};
        const imageAssets = (assets || []).filter((a) => a.kind === "IMAGE" || a.content_type?.startsWith("image/") || a.url);
        return (
          <div className="space-y-2">
            {/* Asset picker */}
            {imageAssets.length > 0 && (
              <div>
                <label className={labelCls}>Choose from Assets</label>
                <div className="flex gap-1.5 flex-wrap mt-1">
                  {imageAssets.map((a) => {
                    const imgUrl = a.url?.startsWith("http") ? a.url : `/api/assets/file/${a.id}`;
                    const isSelected = c.url === imgUrl || c.url === a.url;
                    return (
                      <button
                        key={a.id}
                        onClick={() => {
                          updateContent("url", imgUrl);
                          if (!c.alt) updateContent("alt", a.name);
                        }}
                        className={`w-16 h-12 border rounded-sm overflow-hidden flex items-center justify-center p-0.5 transition-all ${
                          isSelected ? "border-[#0F6CBD] ring-1 ring-[#0F6CBD]" : "border-[#E1DFDD] hover:border-[#0F6CBD]/50"
                        }`}
                        title={a.name}
                        data-testid={`pick-asset-${a.id}`}
                      >
                        <img src={imgUrl} alt={a.name} className="max-w-full max-h-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <label className={labelCls}>Image URL {imageAssets.length > 0 ? "(or enter manually)" : ""}</label>
              <input value={c.url || ""} onChange={(e) => updateContent("url", e.target.value)} className={inputCls} placeholder="https://..." />
            </div>
            {c.url && (
              <div className="border border-[#E1DFDD] rounded-sm p-2 bg-white">
                <img src={c.url} alt={c.alt || ""} className="max-h-16 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Alt Text</label>
                <input value={c.alt || ""} onChange={(e) => updateContent("alt", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Width</label>
                <input value={c.width || (block.type === "COMPANY_LOGO" ? 180 : "100%")} onChange={(e) => updateContent("width", e.target.value)} className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Link URL</label>
              <input value={c.href || ""} onChange={(e) => updateContent("href", e.target.value)} className={inputCls} placeholder="https://..." />
            </div>
          </div>
        );
      })()}

      {/* Divider */}
      {block.type === "DIVIDER" && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={labelCls}>Color</label>
            <input value={block.settings?.color || "#E1DFDD"} onChange={(e) => updateSetting("color", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Width</label>
            <input value={block.settings?.width || "100%"} onChange={(e) => updateSetting("width", e.target.value)} className={inputCls} />
          </div>
        </div>
      )}

      {/* Spacer */}
      {block.type === "SPACER" && (
        <div>
          <label className={labelCls}>Height</label>
          <input value={block.settings?.height || "12px"} onChange={(e) => updateSetting("height", e.target.value)} className={inputCls} />
        </div>
      )}

      {/* Social Icons */}
      {["SOCIAL_ICONS", "SOCIAL_LINKS"].includes(block.type) && (
        <div className="space-y-2">
          {(block.items || []).map((item, idx) => (
            <div key={idx} className="flex gap-1 items-end">
              <div className="flex-1">
                <label className={labelCls}>{idx === 0 ? "Platform" : ""}</label>
                <input value={item.label || item.platform || ""} onChange={(e) => updateItem(idx, "label", e.target.value)} className={inputCls} placeholder="LinkedIn" />
              </div>
              <div className="flex-1">
                <label className={labelCls}>{idx === 0 ? "URL" : ""}</label>
                <input value={item.url || ""} onChange={(e) => updateItem(idx, "url", e.target.value)} className={inputCls} placeholder="https://..." />
              </div>
              <button onClick={() => removeItem(idx)} className="text-[#A4262C] p-1 shrink-0"><Trash2 size={12} /></button>
            </div>
          ))}
          <button onClick={() => addItem({ platform: "", url: "", label: "" })} className="text-xs text-[#0F6CBD] flex items-center gap-1 hover:underline">
            <Plus size={12} /> Add icon
          </button>
        </div>
      )}

      {/* Membership Badges */}
      {block.type === "MEMBERSHIP_BADGES" && (
        <div className="space-y-2">
          {(block.items || []).map((item, idx) => (
            <div key={idx} className="space-y-1 p-2 bg-white border border-[#E1DFDD] rounded-sm">
              <div className="grid grid-cols-2 gap-1">
                <div>
                  <label className={labelCls}>Image URL</label>
                  <input value={item.url || ""} onChange={(e) => updateItem(idx, "url", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Alt / Label</label>
                  <input value={item.alt || ""} onChange={(e) => updateItem(idx, "alt", e.target.value)} className={inputCls} />
                </div>
              </div>
              <div className="flex gap-1 items-end">
                <div className="flex-1">
                  <label className={labelCls}>Link</label>
                  <input value={item.href || ""} onChange={(e) => updateItem(idx, "href", e.target.value)} className={inputCls} />
                </div>
                <button onClick={() => removeItem(idx)} className="text-[#A4262C] p-1"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
          <button onClick={() => addItem({ url: "", alt: "Badge", href: "", height: 40 })} className="text-xs text-[#0F6CBD] flex items-center gap-1 hover:underline">
            <Plus size={12} /> Add badge
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Main Template Builder ── */
export default function TemplateBuilder() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [blocks, setBlocks] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [templateName, setTemplateName] = useState("New Template");
  const [templateMode, setTemplateMode] = useState("FULL");
  const [referenceImage, setReferenceImage] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [started, setStarted] = useState(false);
  const [assets, setAssets] = useState([]);
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  // Load assets + check for edit mode
  useEffect(() => {
    axios.get(`${API}/assets/`).then((r) => setAssets(r.data.assets || [])).catch(() => {});

    const params = new URLSearchParams(window.location.search);
    const editId = params.get("edit");
    if (editId) {
      loadExistingTemplate(editId);
    }
  }, []);

  const loadExistingTemplate = async (templateId) => {
    try {
      const resp = await axios.get(`${API}/templates/${templateId}/blocks`);
      const data = resp.data;
      setEditingTemplateId(data.id);
      setTemplateName(data.name);
      setTemplateMode(data.mode);
      setBlocks(data.blocks || []);
      setStarted(true);
    } catch (err) {
      console.error("Failed to load template:", err);
    }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setBlocks((prev) => {
        const oldIdx = prev.findIndex((b) => b.id === active.id);
        const newIdx = prev.findIndex((b) => b.id === over.id);
        return arrayMove(prev, oldIdx, newIdx);
      });
    }
  }, []);

  const analyzeImage = async (file) => {
    setAnalyzing(true);
    setReferenceImage(URL.createObjectURL(file));
    setStarted(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await axios.post(`${API}/templates/analyze-image`, fd);
      if (resp.data.blocks?.length) {
        setBlocks(resp.data.blocks);
      }
    } catch (err) {
      console.error("Analysis failed:", err);
    }
    setAnalyzing(false);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) analyzeImage(file);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) analyzeImage(file);
  };

  const addBlock = (type) => {
    const newBlock = createNewBlock(type);
    setBlocks((prev) => [...prev, newBlock]);
    setSelectedBlockId(newBlock.id);
    setShowAddMenu(false);
  };

  const deleteBlock = (id) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    if (selectedBlockId === id) setSelectedBlockId(null);
  };

  const updateBlock = (updated) => {
    setBlocks((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      if (editingTemplateId) {
        await axios.put(`${API}/templates/${editingTemplateId}/builder`, {
          name: templateName,
          mode: templateMode,
          blocks: blocks,
        });
      } else {
        await axios.post(`${API}/templates/from-builder`, {
          name: templateName,
          mode: templateMode,
          blocks: blocks,
        });
      }
      navigate("/templates");
    } catch (err) {
      console.error("Save failed:", err);
    }
    setSaving(false);
  };

  const previewHtml = blocks.map(renderBlockPreview).join("\n");
  const selectedBlock = blocks.find((b) => b.id === selectedBlockId);

  // ─── Initial upload state ───
  if (!started) {
    return (
      <div className="p-8 max-w-[1600px]" data-testid="template-builder-page">
        <button onClick={() => navigate("/templates")} className="flex items-center gap-1 text-sm text-[#0F6CBD] hover:underline mb-6" data-testid="back-to-templates-btn">
          <ArrowLeft size={14} /> Back to templates
        </button>
        <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Template Builder</h1>
        <p className="text-sm text-[#5E5E5E] mt-1">Create a new signature template from a reference image or start from scratch</p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
          {/* AI from image */}
          <div
            className="border-2 border-dashed border-[#0F6CBD] rounded-sm p-8 text-center cursor-pointer hover:bg-[#EBF3FC] transition-colors"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            data-testid="image-drop-zone"
          >
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
            <Sparkles size={36} className="mx-auto text-[#0F6CBD]" />
            <p className="text-sm font-medium text-[#0F6CBD] mt-4">AI-Powered: From Image</p>
            <p className="text-xs text-[#5E5E5E] mt-1">
              Drop a screenshot of an existing signature or click to upload. AI will analyze and create blocks.
            </p>
          </div>

          {/* Start blank */}
          <div
            className="border-2 border-dashed border-[#E1DFDD] rounded-sm p-8 text-center cursor-pointer hover:bg-[#F3F2F1] transition-colors"
            onClick={() => { setStarted(true); setBlocks([createNewBlock("SENDER_DETAILS")]); }}
            data-testid="start-blank-btn"
          >
            <Plus size={36} className="mx-auto text-[#5E5E5E]" />
            <p className="text-sm font-medium text-[#171717] mt-4">Start from Scratch</p>
            <p className="text-xs text-[#5E5E5E] mt-1">
              Begin with an empty template and add blocks manually.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Builder state ───
  return (
    <div className="p-6 h-full flex flex-col max-w-[1600px]" data-testid="template-builder-active">
      {/* Top bar */}
      <div className="flex items-center gap-3 mb-4 shrink-0">
        <button onClick={() => navigate("/templates")} className="text-[#5E5E5E] hover:text-[#171717]" data-testid="builder-back-btn">
          <ArrowLeft size={18} />
        </button>
        <Input
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          className="max-w-xs font-heading font-semibold border-[#E1DFDD] rounded-sm"
          data-testid="builder-template-name"
        />
        <select
          value={templateMode}
          onChange={(e) => setTemplateMode(e.target.value)}
          className="border border-[#E1DFDD] rounded-sm px-2 py-1.5 text-sm"
          data-testid="builder-template-mode"
        >
          <option value="FULL">FULL</option>
          <option value="SHORT">SHORT</option>
        </select>
        <div className="flex-1" />
        <Button onClick={saveTemplate} disabled={saving || blocks.length === 0} className="bg-[#0F6CBD] text-white hover:bg-[#005A9E] rounded-sm" data-testid="builder-save-btn">
          <Save size={14} className="mr-2" /> {saving ? "Saving..." : editingTemplateId ? "Update Template" : "Save Template"}
        </Button>
      </div>

      {/* Main split */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[#E1DFDD] rounded-sm overflow-hidden min-h-0">
        {/* Left panel: blocks */}
        <div className="col-span-1 lg:col-span-4 border-r border-[#E1DFDD] bg-[#FAFAFA] flex flex-col min-h-0">
          {/* Reference image */}
          {referenceImage && (
            <div className="p-3 border-b border-[#E1DFDD]">
              <p className="text-[10px] uppercase tracking-wider text-[#5E5E5E] font-semibold mb-2">Reference</p>
              <img src={referenceImage} alt="Reference" className="max-h-24 rounded-sm border border-[#E1DFDD]" />
            </div>
          )}

          {analyzing && (
            <div className="p-6 text-center" data-testid="analyzing-indicator">
              <Loader2 size={24} className="mx-auto animate-spin text-[#0F6CBD]" />
              <p className="text-sm text-[#5E5E5E] mt-2">Analyzing signature...</p>
            </div>
          )}

          {/* Block list */}
          <div className="flex-1 overflow-y-auto p-3 space-y-1.5" data-testid="block-list">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                {blocks.map((block) => (
                  <div key={block.id}>
                    <SortableBlock
                      block={block}
                      isSelected={selectedBlockId === block.id}
                      onSelect={setSelectedBlockId}
                      onDelete={deleteBlock}
                    />
                    {selectedBlockId === block.id && (
                      <BlockConfig block={block} onChange={updateBlock} assets={assets} />
                    )}
                  </div>
                ))}
              </SortableContext>
            </DndContext>
          </div>

          {/* Add block */}
          <div className="p-3 border-t border-[#E1DFDD] relative">
            <Button
              variant="outline"
              onClick={() => setShowAddMenu(!showAddMenu)}
              className="w-full border-[#E1DFDD] rounded-sm text-xs justify-center"
              data-testid="add-block-btn"
            >
              <Plus size={14} className="mr-1" /> Add Block
            </Button>
            {showAddMenu && (
              <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-[#E1DFDD] rounded-sm shadow-sm max-h-64 overflow-y-auto z-10" data-testid="add-block-menu">
                {Object.entries(BLOCK_TYPES).map(([type, def]) => {
                  const Icon = def.icon;
                  return (
                    <button
                      key={type}
                      onClick={() => addBlock(type)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#5E5E5E] hover:bg-[#EBF3FC] hover:text-[#0F6CBD] transition-colors"
                      data-testid={`add-block-${type}`}
                    >
                      <div className="w-1.5 h-4 rounded-sm" style={{ backgroundColor: def.color }} />
                      <Icon size={13} />
                      <span>{def.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right panel: preview */}
        <div className="col-span-1 lg:col-span-8 bg-white flex flex-col min-h-0">
          <div className="px-5 py-3 border-b border-[#E1DFDD] flex items-center justify-between shrink-0">
            <h3 className="text-xs font-semibold uppercase tracking-[0.05em] text-[#5E5E5E]">Live Preview</h3>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#5E5E5E]">{blocks.length} blocks</span>
              {blocks.length > 0 && (
                <button
                  onClick={() => { navigator.clipboard.writeText(previewHtml); toast.success("HTML copied to clipboard"); }}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] text-[#0F6CBD] hover:bg-[#EBF3FC] rounded-sm transition-colors"
                  data-testid="copy-html-btn"
                >
                  <Copy size={11} /> Copy HTML
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-8 bg-[#FAFAFA]" data-testid="builder-preview">
            {blocks.length === 0 ? (
              <div className="border border-dashed border-[#E1DFDD] rounded-sm p-12 text-center text-sm text-[#5E5E5E]">
                Add blocks to see the signature preview
              </div>
            ) : (
              <div className="bg-white border border-[#E1DFDD] rounded-sm p-6 max-w-[650px]">
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
